import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@ngneat/transloco';
import { TranslocoRootModule } from 'src/app/transloco-root.module';
import { AuthService } from 'src/app/service/auth.service';
import { GlobalService } from 'src/app/service/global.service';
import { AccountService } from 'src/app/service/account.service';
import { TunerService } from 'src/app/service/tuner.service';
import { Plan } from 'src/app/model/response/plan-response';
import { TunerPublic } from 'src/app/model/response/tuner-response';
import { DowngradeAffiliatesModalComponent } from 'src/app/share/downgrade-affiliates-modal/downgrade-affiliates-modal.component';

/**
 * Célula da comparação. Três estados, porque três coisas diferentes acontecem: o
 * plano traz um valor numérico (`value`), tem o recurso booleano (`yes`), ou não
 * tem a linha (`no`). `text` só é preenchido em `value`; nos outros fica vazio —
 * é um campo só em vez de união discriminada porque `ngSwitch` não estreita tipo
 * em template Angular, e `strictTemplates` recusaria o acesso.
 */
interface ComparisonCell {
  kind: 'value' | 'yes' | 'no';
  text: string;
}

/** Uma linha da matriz: um recurso, com uma célula por plano na ordem de `orderedPlans`. */
interface ComparisonRow {
  key: string;
  /** Texto vindo da API (`PlanFeature.description`). Vazio quando a linha é nossa. */
  label: string;
  /**
   * Chave de i18n, para as linhas que não vêm da API (hoje só o armazenamento). O
   * template traduz na hora: traduzir aqui congelaria o rótulo na língua ativa no
   * momento da carga, porque a matriz é montada uma única vez.
   */
  labelKey: string | null;
  cells: ComparisonCell[];
  /** Idêntica em todos os planos — não distingue nada, então o filtro pode escondê-la. */
  same: boolean;
}

@Component({
  selector: 'app-pro-feature-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, ProgressSpinnerModule, TranslocoRootModule, DowngradeAffiliatesModalComponent],
  templateUrl: './pro-feature-modal.component.html',
  styleUrl: './pro-feature-modal.component.css',
})
export class ProFeatureModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Input() featureName: string | null = null;
  @Input() currentPlanId: string | null = null;
  @Input() currentPlanExpired: boolean = false;
  @Input() billingType: 'stripe' | 'manual' | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() planUpgraded = new EventEmitter<void>();

  // Passo de afiliados no downgrade — só existe enquanto este plano estiver setado.
  downgradeAffiliatesPlanId: string | null = null;

  /**
   * Profile id de tuner do usuário logado — só preenchido quando ele é tuner. Sem isso não há
   * como saber se um downgrade dele afeta afiliados. Antes vinha de um @Input que só 1 dos 5
   * lugares que abrem este modal preenchia; os outros 4 (inclusive o modal de gate que esta
   * própria feature criou) deixavam nulo e o downgrade pulava direto pro checkout, sem preview
   * nem seleção obrigatória. Resolvido aqui dentro, na mesma fonte que o resto da conta usa
   * (AccountService.getAccount → accountType/tunerProfileId), pra não existirem duas fontes.
   * Buscado na hora do clique em "fazer downgrade" (goToSubscription), não na abertura do
   * modal — evita correr em paralelo com o carregamento dos planos e o usuário conseguir
   * clicar antes da resposta chegar. Não é `private`: o template usa para passar ao modal de
   * afiliados.
   */
  resolvedTunerId: string | null = null;

  plans: Plan[] = [];
  loading: boolean = false;
  billingAnnual: boolean = false;
  checkoutLoading: boolean = false;
  checkoutError: string = '';
  showConfirm: boolean = false;
  pendingPlanId: string | null = null;
  upgradeSuccess: boolean = false;
  // A tela de sucesso é a mesma do upgrade (upgradeSuccess); estes dois campos só
  // trocam o texto exibido nela quando o checkout devolveu um downgrade agendado.
  downgradeScheduled: boolean = false;
  downgradeEffectiveAt: string | null = null;
  portalLoading: boolean = false;

  // Tuner selection step
  showTunerStep: boolean = false;
  tunerStepLoading: boolean = false;
  tunerOptions: TunerPublic[] = [];
  selectedTunerId: string | null = null;
  tunerMode: 'list' | 'code' = 'list';
  tunerInviteInput: string = '';
  inviteValidating: boolean = false;
  inviteError: string = '';
  validatedTuner: TunerPublic | null = null;

  // ─── Comparação de planos ─────────────────────────────────────────────────
  // Uma matriz: cada recurso escrito uma vez, em ordem canônica, com uma coluna
  // por plano. Substituiu os quatro cartões que repetiam a lista inteira.
  onlyDifferences = false;

  /** Planos por preço mensal crescente — a ordem da API não é garantida. */
  orderedPlans: Plan[] = [];
  capacityRows: ComparisonRow[] = [];
  featureRows: ComparisonRow[] = [];
  visibleCapacityRows: ComparisonRow[] = [];
  visibleFeatureRows: ComparisonRow[] = [];
  hiddenRowCount = 0;

  // ─── Layout estreito ──────────────────────────────────────────────────────
  // Abaixo deste ponto a matriz de quatro colunas não cabe: o mínimo dela é
  // 820px e ainda há o padding do diálogo. Em vez de rolar na horizontal e
  // mostrar um plano por vez sem contexto, a tela troca para uma coluna de
  // valores só, com seletor de plano — as linhas e a ordem continuam as mesmas,
  // então comparar é trocar de aba e ver o número mudar no mesmo lugar.
  private static readonly NARROW_QUERY = '(max-width: 860px)';
  private narrowMq: MediaQueryList | null = null;
  private onNarrowChange = (e: MediaQueryListEvent) => { this.isNarrow = e.matches; };

  isNarrow = false;
  /** Índice em `orderedPlans` do plano exibido no layout estreito. */
  narrowPlanIndex = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private globalService: GlobalService,
    private accountService: AccountService,
    private tunerService: TunerService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.narrowMq = window.matchMedia(ProFeatureModalComponent.NARROW_QUERY);
    this.isNarrow = this.narrowMq.matches;
    this.narrowMq.addEventListener('change', this.onNarrowChange);
  }

  ngOnDestroy(): void {
    this.narrowMq?.removeEventListener('change', this.onNarrowChange);
    this.narrowMq = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && this.plans.length === 0) {
      this.loadPlans();
    }
    if (changes['visible']?.currentValue === false) {
      this.resetState();
    }
  }

  /**
   * A vitrine resolve moeda por GeoIP por padrão; quando a assinatura ATIVA do usuário já tem
   * moeda definida, ela tem que coincidir com a moeda usada na cobrança (GetActiveByUserId usa a
   * moeda da assinatura). Fora do status ativo (expirada/inexistente), o backend trataria uma
   * nova assinatura como primeira compra e cairia no GeoIP — a tela precisa concordar, então não
   * manda override. Resolvido aqui dentro (mesmo padrão do downgrade acima), não por @Input.
   */
  private loadPlans(): void {
    this.loading = true;
    const userId = this.authService.getUserInformations();
    this.accountService.getAccount(userId).subscribe({
      next: (account) => {
        const currency = account?.planInformation?.status === 'active'
          ? account.planInformation.currency ?? undefined
          : undefined;
        this.fetchPlans(currency);
      },
      // getAccount falhando não pode travar a vitrine: segue sem override (GeoIP), como hoje.
      error: () => this.fetchPlans(undefined),
    });
  }

  private fetchPlans(currency: string | undefined): void {
    this.globalService.getPlans(currency).subscribe({
      next: (plans) => {
        this.plans = plans;
        this.buildComparison();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private resetState(): void {
    this.checkoutError = '';
    this.showConfirm = false;
    this.pendingPlanId = null;
    this.upgradeSuccess = false;
    this.downgradeScheduled = false;
    this.downgradeEffectiveAt = null;
    this.resolvedTunerId = null;
    this.portalLoading = false;
    this.checkoutLoading = false;
    this.showTunerStep = false;
    this.tunerStepLoading = false;
    this.selectedTunerId = null;
    this.tunerMode = 'list';
    this.tunerInviteInput = '';
    this.inviteValidating = false;
    this.inviteError = '';
    this.validatedTuner = null;
    this.onlyDifferences = false;
    this.downgradeAffiliatesPlanId = null;
    this.applyRowFilter();
  }

  toggleOnlyDifferences(): void {
    this.onlyDifferences = !this.onlyDifferences;
    this.applyRowFilter();
  }

  isCurrentPlan(plan: Plan): boolean {
    if (this.currentPlanExpired) return false;
    return !!this.currentPlanId && plan.planId === this.currentPlanId;
  }

  isManualCurrentPlan(plan: Plan): boolean {
    const isPaidPlan = plan.price > 0 || plan.priceAnnual > 0;
    return this.isCurrentPlan(plan) && this.billingType === 'manual' && isPaidPlan;
  }

  isDowngrade(plan: Plan): boolean {
    const current = this.plans.find(p => p.planId === this.currentPlanId);
    if (!current) return false;
    const currentVal = this.billingAnnual ? current.priceAnnual : current.price;
    const targetVal = this.billingAnnual ? plan.priceAnnual : plan.price;
    return targetVal < currentVal;
  }

  /** O plano pendente na tela de confirmação é um downgrade? Decide qual texto exibir ali. */
  isPendingDowngrade(): boolean {
    const plan = this.plans.find(p => p.planId === this.pendingPlanId);
    return !!plan && this.isDowngrade(plan);
  }

  getPendingPlanName(): string {
    return this.plans.find(p => p.planId === this.pendingPlanId)?.name ?? '';
  }

  getCurrentPlanPrice(): string {
    const plan = this.plans.find(p => p.planId === this.currentPlanId);
    if (!plan) return '';
    if (plan.price === 0) return this.translocoService.translate('proModal.planCard.free');
    const value = this.billingAnnual ? plan.priceAnnual : plan.price;
    if (value == null) return this.translocoService.translate('proModal.planCard.onRequest');
    return this.formatCurrency(value, plan.currency);
  }

  getPendingPlanPrice(): string {
    const plan = this.plans.find(p => p.planId === this.pendingPlanId);
    if (!plan) return '';
    if (plan.price === 0) return this.translocoService.translate('proModal.planCard.free');
    const value = this.billingAnnual ? plan.priceAnnual : plan.price;
    if (value == null) return this.translocoService.translate('proModal.planCard.onRequest');
    return this.formatCurrency(value, plan.currency);
  }

  getPriceDiff(): string {
    const current = this.plans.find(p => p.planId === this.currentPlanId);
    const pending = this.plans.find(p => p.planId === this.pendingPlanId);
    if (!current || !pending) return '';
    const currentVal = this.billingAnnual ? current.priceAnnual : current.price;
    const pendingVal = this.billingAnnual ? pending.priceAnnual : pending.price;
    if (currentVal == null || pendingVal == null) return '';
    const diff = pendingVal - currentVal;
    const currency = pending.currency || current.currency || 'BRL';
    const formatted = this.formatCurrency(Math.abs(diff), currency);
    return diff >= 0 ? `+ ${formatted}` : `- ${formatted}`;
  }

  isPriceDiffPositive(): boolean {
    const current = this.plans.find(p => p.planId === this.currentPlanId);
    const pending = this.plans.find(p => p.planId === this.pendingPlanId);
    if (!current || !pending) return true;
    const currentVal = this.billingAnnual ? current.priceAnnual : current.price;
    const pendingVal = this.billingAnnual ? pending.priceAnnual : pending.price;
    return pendingVal >= currentVal;
  }

  isFeatureEnabled(value: string): boolean {
    return value !== 'false' && value !== '0';
  }

  /**
   * Monta a matriz a partir dos planos, uma vez por carga. Antes o template montava
   * a lista por plano, e por isso as linhas não alinhavam entre os cartões: cada
   * plano trazia a própria ordem da API, e um recurso ausente simplesmente não
   * aparecia, em vez de aparecer como não incluído.
   */
  private buildComparison(): void {
    this.orderedPlans = [...this.plans].sort((a, b) => a.price - b.price);

    // Ordem canônica dos recursos: primeira aparição varrendo do plano mais alto
    // para o mais baixo. O de cima tem a lista mais completa, então a ordem dele
    // domina e os de baixo só acrescentam o que ele não tiver. A identidade é a
    // `key` da feature, não a `description`, que é texto livre.
    const keys: string[] = [];
    const labels = new Map<string, string>();
    [...this.orderedPlans].reverse().forEach((plan) =>
      plan.features.forEach((f) => {
        if (!labels.has(f.key)) {
          labels.set(f.key, f.description);
          keys.push(f.key);
        }
      }),
    );

    const rows = keys.map((key) => this.buildRow(key, labels.get(key)!));

    // Capacidade é a linha em que algum plano traz número; recurso é a binária. A
    // distinção é real: uma se compara por magnitude, a outra por presença.
    const isCapacity = (r: ComparisonRow) => r.cells.some((c) => c.kind === 'value');

    // Armazenamento não é feature, é campo do plano — entra como primeira linha
    // de capacidade pra não ficar fora da comparação.
    const diskCells: ComparisonCell[] = this.orderedPlans.map((p) => ({
      kind: 'value',
      text: this.formatDiskSpace(p.diskSpace),
    }));
    const diskRow: ComparisonRow = {
      key: '__disk',
      label: '',
      labelKey: 'proModal.compare.storageRow',
      cells: diskCells,
      same: this.rowSame(diskCells),
    };

    this.capacityRows = [diskRow, ...rows.filter(isCapacity)];
    this.featureRows = rows.filter((r) => !isCapacity(r));
    this.hiddenRowCount = [...this.capacityRows, ...this.featureRows].filter((r) => r.same).length;

    // No estreito, abre no plano do usuário: é o contexto dele. Sem plano
    // (Free/expirado), abre no primeiro, que é o mais barato.
    const current = this.orderedPlans.findIndex((p) => this.isCurrentPlan(p));
    this.narrowPlanIndex = current >= 0 ? current : 0;

    this.applyRowFilter();
  }

  private buildRow(key: string, label: string): ComparisonRow {
    const cells = this.orderedPlans.map((plan): ComparisonCell => {
      const f = plan.features.find((x) => x.key === key);
      if (!f) return { kind: 'no', text: '' };
      if (f.value === 'true') return { kind: 'yes', text: '' };
      if (!this.isFeatureEnabled(f.value)) return { kind: 'no', text: '' };
      return { kind: 'value', text: f.value };
    });
    return { key, label, labelKey: null, cells, same: this.rowSame(cells) };
  }

  private rowSame(cells: ComparisonCell[]): boolean {
    const id = (c: ComparisonCell) => (c.kind === 'value' ? 'v:' + c.text : c.kind);
    if (cells.length === 0) return true;
    return cells.every((c) => id(c) === id(cells[0]));
  }

  private applyRowFilter(): void {
    const keep = (r: ComparisonRow) => !this.onlyDifferences || !r.same;
    this.visibleCapacityRows = this.capacityRows.filter(keep);
    this.visibleFeatureRows = this.featureRows.filter(keep);
  }

  private formatCurrency(value: number, currency: string): string {
    const locale = currency === 'USD' ? 'en-US' : 'pt-BR';
    return value.toLocaleString(locale, { style: 'currency', currency });
  }

  isFree(plan: Plan): boolean {
    return plan.price === 0;
  }

  isPriceUnavailable(plan: Plan): boolean {
    if (this.isFree(plan)) return false;
    const value = this.billingAnnual ? plan.priceAnnual : plan.price;
    return value == null;
  }

  formatPrice(plan: Plan): string {
    if (this.isFree(plan)) return this.translocoService.translate('proModal.planCard.free');
    const value = this.billingAnnual ? plan.priceAnnual : plan.price;
    return this.formatCurrency(value, plan.currency);
  }

  toggleBilling(annual: boolean): void {
    this.billingAnnual = annual;
  }

  formatDiskSpace(kb: number): string {
    const gb = kb / (1024 * 1024);
    return `${gb % 1 === 0 ? gb : gb.toFixed(1)} GB`;
  }

  goToSubscription(planId: string): void {
    this.checkoutError = '';
    this.pendingPlanId = planId;

    // Downgrade de quem é tuner pode obrigar a escolher afiliados, ou ao menos
    // avisar da perda de cashback — sempre antes do Stripe. Sem afiliados
    // impactados (preview sem seleção e sem cashback), segue direto.
    const targetPlan = this.plans.find(p => p.planId === planId);
    if (targetPlan && this.isDowngrade(targetPlan)) {
      this.resolveTunerIdThenCheckDowngrade(planId);
      return;
    }

    this.continueGoToSubscription(planId);
  }

  /**
   * Só entra aqui num downgrade. Resolve se o usuário logado é ele mesmo um tuner na hora do
   * clique — não dá pra confiar num valor pré-carregado na abertura do modal, porque o usuário
   * pode clicar antes da resposta chegar.
   */
  private resolveTunerIdThenCheckDowngrade(planId: string): void {
    this.checkoutLoading = true;
    const userId = this.authService.getUserInformations();
    this.accountService.getAccount(userId).subscribe({
      next: (account) => {
        this.checkoutLoading = false;
        this.resolvedTunerId = account.accountType === 'tuner' ? (account.tunerProfileId ?? null) : null;
        if (this.resolvedTunerId) {
          this.checkDowngradeAffiliates(planId);
        } else {
          this.continueGoToSubscription(planId);
        }
      },
      error: () => {
        // Mesmo raciocínio do erro do getDowngradePreview logo abaixo: sem saber se o
        // usuário é tuner, seguir cegamente arrisca pular a seleção obrigatória de
        // afiliados. Trava no modal e deixa tentar de novo.
        this.checkoutLoading = false;
        this.checkoutError = this.translocoService.translate('proModal.errors.downgradePreviewFailed');
      },
    });
  }

  private checkDowngradeAffiliates(planId: string): void {
    this.checkoutLoading = true;
    this.tunerService.getDowngradePreview(this.resolvedTunerId!, planId).subscribe({
      next: (preview) => {
        this.checkoutLoading = false;
        if (preview.requiresSelection || preview.monthlyCashbackTotal > 0) {
          this.downgradeAffiliatesPlanId = planId;
        } else {
          this.continueGoToSubscription(planId);
        }
      },
      error: () => {
        // Diferente do getUserTuner logo abaixo: aqui NÃO dá pra seguir sem
        // resposta. Se o preview falhar num downgrade que exigiria seleção,
        // nenhuma seleção é gravada e, na virada, nada é removido — o tuner
        // fica acima do teto do plano novo, exatamente o que a seleção
        // obrigatória existe para impedir. Trava no modal e deixa tentar de novo.
        this.checkoutLoading = false;
        this.checkoutError = this.translocoService.translate('proModal.errors.downgradePreviewFailed');
      },
    });
  }

  private continueGoToSubscription(planId: string): void {
    const userId = this.authService.getUserInformations();
    this.tunerStepLoading = true;

    this.tunerService.getUserTuner(userId).subscribe({
      next: () => {
        // já tem tuner → pula a etapa de seleção
        this.tunerStepLoading = false;
        this.proceedAfterTunerCheck(planId, false);
      },
      error: (err: HttpErrorResponse) => {
        this.tunerStepLoading = false;
        if (err.status === 404) {
          // sem tuner → mostra a etapa de seleção
          this.loadPublicTunersForPlan(planId);
          this.showTunerStep = true;
        } else {
          // outro erro → segue o fluxo sem travar o checkout
          this.proceedAfterTunerCheck(planId, false);
        }
      },
    });
  }

  /** O modal de afiliados já chamou o confirm — só falta o checkout de sempre. */
  onDowngradeAffiliatesConfirmed(): void {
    const planId = this.downgradeAffiliatesPlanId!;
    this.downgradeAffiliatesPlanId = null;
    this.continueGoToSubscription(planId);
  }

  onDowngradeAffiliatesCancelled(): void {
    this.downgradeAffiliatesPlanId = null;
    this.pendingPlanId = null;
  }

  private proceedAfterTunerCheck(planId: string, skipConfirm: boolean = false): void {
    const currentPlan = this.plans.find(p => p.planId === this.currentPlanId);
    const hasActivePaidPlan = !!currentPlan && currentPlan.price > 0 && !this.currentPlanExpired && this.billingType === 'stripe';
    if (!skipConfirm && hasActivePaidPlan) {
      this.showConfirm = true;
    } else {
      this.callCheckout(planId);
    }
  }

  /**
   * A API recebe o planId e devolve só os tuners elegíveis, com vaga e que
   * aceitam o tier do plano escolhido. O filtro por nome de plano que existia
   * aqui foi REMOVIDO de propósito: a regra vive num único lugar, no backend.
   */
  private loadPublicTunersForPlan(planId: string): void {
    this.tunerService.getPublicTuners(planId).subscribe({
      next: (list) => {
        this.tunerOptions = list;
      },
      error: () => {
        this.tunerOptions = [];
      },
    });
  }

  validateInviteCode(): void {
    if (!this.tunerInviteInput.trim()) return;
    this.inviteValidating = true;
    this.inviteError = '';
    this.validatedTuner = null;
    this.tunerService.validateInviteCode(this.tunerInviteInput.trim(), this.pendingPlanId ?? undefined).subscribe({
      next: (tuner) => {
        this.validatedTuner = tuner;
        this.inviteValidating = false;
      },
      error: (err: HttpErrorResponse) => {
        this.inviteValidating = false;
        if (err.status === 404) {
          this.inviteError = this.translocoService.translate('proModal.tuner.invalidCode');
        } else if (err.status === 400) {
          // Tuner existe, mas não aceita o tier do plano escolhido.
          this.inviteError = this.translocoService.translate('proModal.tuner.planNotAccepted');
        } else {
          this.inviteError = this.translocoService.translate('proModal.tuner.validateError');
        }
      },
    });
  }

  confirmTunerStep(): void {
    this.showTunerStep = false;
    const currentPlan = this.plans.find(p => p.planId === this.currentPlanId);
    const hasActivePaidPlan = !!currentPlan && currentPlan.price > 0 && !this.currentPlanExpired && this.billingType === 'stripe';
    if (hasActivePaidPlan) {
      this.showConfirm = true;
    } else {
      this.callCheckout(this.pendingPlanId!);
    }
  }

  skipTunerStep(): void {
    this.selectedTunerId = null;
    this.validatedTuner = null;
    this.tunerInviteInput = '';
    this.showTunerStep = false;
    this.proceedAfterTunerCheck(this.pendingPlanId!);
  }

  cancelTunerStep(): void {
    this.showTunerStep = false;
    this.pendingPlanId = null;
    this.selectedTunerId = null;
    this.validatedTuner = null;
    this.tunerInviteInput = '';
  }

  confirmUpgrade(): void {
    if (!this.pendingPlanId) return;
    this.callCheckout(this.pendingPlanId);
  }

  private callCheckout(planId: string): void {
    this.checkoutError = '';
    this.checkoutLoading = true;
    const userId = this.authService.getUserInformations();
    const billingCycle: 'monthly' | 'annual' = this.billingAnnual ? 'annual' : 'monthly';

    const tunerId = this.selectedTunerId ?? undefined;
    const tunerInviteCode = (this.tunerMode === 'code' && this.validatedTuner)
      ? this.tunerInviteInput.trim()
      : undefined;

    this.accountService.checkoutSubscription(userId, planId, billingCycle, tunerId, tunerInviteCode).subscribe({
      next: (response) => {
        this.checkoutLoading = false;
        if (response.checkoutUrl) {
          sessionStorage.setItem('subscriptionReturnUrl', this.router.url);
          window.location.href = response.checkoutUrl;
        } else if (response.upgraded) {
          this.showConfirm = false;
          this.upgradeSuccess = true;
          this.planUpgraded.emit();
        } else if (response.scheduled) {
          // Downgrade agendado: nada foi cobrado nem trocado agora, mas a página de
          // conta precisa recarregar do mesmo jeito (planUpgraded é o único evento
          // que ela escuta para isso).
          this.showConfirm = false;
          this.downgradeScheduled = true;
          this.downgradeEffectiveAt = response.effectiveAt ?? null;
          this.upgradeSuccess = true;
          this.planUpgraded.emit();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.checkoutLoading = false;
        sessionStorage.removeItem('subscriptionReturnUrl');
        if (err.status === 409) {
          this.checkoutError = this.translocoService.translate('proModal.errors.alreadySubscribed');
        } else if (err.status === 400 && err.error?.error === 'stripe price not configured') {
          this.checkoutError = this.translocoService.translate('proModal.errors.priceNotConfigured');
        } else {
          this.checkoutError = this.translocoService.translate('proModal.errors.checkoutFailed');
        }
      },
    });
  }

  openPortalFromModal(): void {
    this.portalLoading = true;
    const userId = this.authService.getUserInformations();
    this.accountService.getSubscriptionPortal(userId).subscribe({
      next: ({ portalUrl }) => {
        window.location.href = portalUrl;
      },
      error: () => {
        this.portalLoading = false;
      },
    });
  }

  cancelConfirm(): void {
    this.showConfirm = false;
    this.pendingPlanId = null;
    this.checkoutError = '';
  }

  close(): void {
    this.visible = false;
    this.resetState();
    this.visibleChange.emit(false);
  }
}
